import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { AVATAR_CATEGORIES, getDefaultAvatar } from '../utils/avatarData';
import { CartoonAvatar } from './GamificationAssets';

export default function AvatarPicker({ currentUrl, onSelect, onClose }) {
    const [selectedCategoryId, setSelectedCategoryId] = useState('students');
    const [seed, setSeed] = useState(Math.random().toString(36).substring(7));
    const [previewUrl, setPreviewUrl] = useState(currentUrl || getDefaultAvatar('User'));

    const currentCat = AVATAR_CATEGORIES.find(c => c.id === selectedCategoryId) || AVATAR_CATEGORIES[0];

    const handleCategoryChange = (catId) => {
        setSelectedCategoryId(catId);
        const cat = AVATAR_CATEGORIES.find(c => c.id === catId);
        if (cat && cat.avatars && cat.avatars.length > 0) {
            setPreviewUrl(cat.avatars[0].url);
        }
    };

    const handleRandomize = () => {
        const newSeed = Math.random().toString(36).substring(7);
        setSeed(newSeed);
        const style = currentCat.styleId || 'micah';
        setPreviewUrl(`https://api.dicebear.com/9.x/${style}/svg?seed=${newSeed}&backgroundColor=transparent`);
    };

    const handleSave = () => {
        if (!previewUrl) return toast.error("Please select an avatar!");
        onSelect(previewUrl);
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(10px)',
            zIndex: 100000, display: 'flex', justifyContent: 'center', alignItems: 'center',
            padding: '20px'
        }}>
            <div style={{
                background: 'var(--bg-primary)',
                width: '100%', maxWidth: '780px', maxHeight: '90vh',
                borderRadius: '24px',
                boxShadow: '0 25px 60px rgba(0,0,0,0.4), inset 0 2px 0 rgba(255,255,255,0.2)',
                border: '1px solid rgba(163, 177, 198, 0.4)',
                overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
                animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
            }}>
                {/* HEADER */}
                <div style={{ padding: '20px 28px', borderBottom: '1px solid rgba(163, 177, 198, 0.2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-secondary)' }}>
                    <div>
                        <h2 style={{ margin: 0, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '20px', fontWeight: '800' }}>
                            <span>✨</span> Choose Your Profile Avatar
                        </h2>
                        <p style={{ margin: '4px 0 0 0', color: 'var(--text-secondary)', fontSize: '12px' }}>
                            Select from 3D Students, Formals, Animals, Memojis, & Cartoonish avatars!
                        </p>
                    </div>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', fontSize: '24px', cursor: 'pointer', color: 'var(--text-secondary)' }}>&times;</button>
                </div>

                <div style={{ display: 'flex', flex: 1, minHeight: '420px', overflow: 'hidden' }}>
                    {/* LEFT PANEL: CATEGORIES */}
                    <div style={{ width: '220px', background: 'var(--bg-secondary)', padding: '16px 12px', display: 'flex', flexDirection: 'column', gap: '8px', borderRight: '1px solid rgba(163, 177, 198, 0.2)', overflowY: 'auto' }}>
                        <div style={{ fontSize: '11px', fontWeight: 'bold', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1px', padding: '0 8px', marginBottom: '4px' }}>Categories</div>
                        
                        {AVATAR_CATEGORIES.map(cat => {
                            const isSelected = selectedCategoryId === cat.id;
                            return (
                                <button
                                    key={cat.id}
                                    onClick={() => handleCategoryChange(cat.id)}
                                    style={{
                                        padding: '10px 12px', borderRadius: '12px', textAlign: 'left',
                                        background: isSelected ? '#3b82f6' : 'transparent',
                                        color: isSelected ? 'white' : 'var(--text-primary)',
                                        border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                                        fontWeight: isSelected ? 'bold' : 'normal',
                                        display: 'flex', flexDirection: 'column', gap: '2px'
                                    }}
                                >
                                    <div style={{ fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cat.label}</div>
                                </button>
                            );
                        })}
                    </div>

                    {/* RIGHT PANEL: PREVIEW & GRID SELECTION */}
                    <div style={{ flex: 1, padding: '24px', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary)', overflowY: 'auto' }}>
                        
                        {/* LIVE PREVIEW BANNER */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '16px 20px', background: 'var(--bg-secondary)', borderRadius: '16px', border: '1px solid rgba(163, 177, 198, 0.2)', marginBottom: '20px' }}>
                            <CartoonAvatar
                                name="Selected Avatar"
                                size={80}
                                avatarUrl={previewUrl}
                            />
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', color: '#3b82f6', letterSpacing: '0.5px' }}>Current Selection</div>
                                <h3 style={{ margin: '2px 0 6px 0', fontSize: '16px', color: 'var(--text-primary)' }}>
                                    {currentCat.avatars.find(a => a.url === previewUrl)?.name || 'Custom Character'}
                                </h3>
                                {currentCat.isGenerative && (
                                    <button
                                        onClick={handleRandomize}
                                        style={{
                                            padding: '6px 14px', background: '#3b82f6', color: 'white',
                                            border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold',
                                            cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px',
                                            boxShadow: '0 2px 8px rgba(59,130,246,0.3)'
                                        }}
                                    >
                                        🎲 Randomize Character
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* CATEGORY TITLE & AVATAR GRID */}
                        <div style={{ marginBottom: '10px' }}>
                            <h4 style={{ margin: '0 0 4px 0', color: 'var(--text-primary)', fontSize: '15px' }}>{currentCat.label}</h4>
                            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '12px' }}>{currentCat.description}</p>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '14px', padding: '10px 0' }}>
                            {currentCat.avatars.map(item => {
                                const isSel = previewUrl === item.url;
                                return (
                                    <div
                                        key={item.id}
                                        onClick={() => setPreviewUrl(item.url)}
                                        title={item.name}
                                        style={{
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                                            padding: '8px', borderRadius: '16px', cursor: 'pointer',
                                            background: isSel ? 'rgba(59, 130, 246, 0.15)' : 'var(--bg-secondary)',
                                            border: isSel ? '2px solid #3b82f6' : '1px solid rgba(163, 177, 198, 0.3)',
                                            boxShadow: isSel ? '0 4px 15px rgba(59,130,246,0.3)' : 'var(--neu-shadow)',
                                            transition: 'all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                            transform: isSel ? 'scale(1.05)' : 'scale(1)'
                                        }}
                                    >
                                        <CartoonAvatar
                                            name={item.name}
                                            size={54}
                                            avatarUrl={item.url}
                                        />
                                        <span style={{ fontSize: '10px', fontWeight: 'bold', color: isSel ? '#3b82f6' : 'var(--text-secondary)', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                                            {item.name}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* FOOTER */}
                <div style={{ padding: '16px 28px', background: 'var(--bg-secondary)', borderTop: '1px solid rgba(163, 177, 198, 0.2)', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                    <button onClick={onClose} style={{ padding: '10px 20px', background: 'transparent', color: 'var(--text-primary)', border: 'none', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer' }}>Cancel</button>
                    <button onClick={handleSave} style={{ padding: '10px 24px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 15px rgba(59,130,246,0.4)' }}>✅ Save Avatar</button>
                </div>
            </div>
            <style>{`
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(30px) scale(0.95); }
                    to { opacity: 1; transform: translateY(0) scale(1); }
                }
            `}</style>
        </div>
    );
}
